import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Loader2, Droplets, Power, Gift, Fuel } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function CouponsPanel() {
  const [pureEnabled, setPureEnabled] = useState(true);
  const [pureBusy, setPureBusy] = useState(false);
  const [loyaltyRate, setLoyaltyRate] = useState(2);
  const [loyaltyBusy, setLoyaltyBusy] = useState(false);
  const [faf, setFaf] = useState({ enabled: false, rate: 0 });
  const [fafBusy, setFafBusy] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", min_amount: "", max_redemptions: "", expires_at: "", active: true });

  const load = useCallback(async () => {
    api.get("/service/config").then(({ data }) => { setPureEnabled(data.pure_water_enabled !== false); setLoyaltyRate(data.loyalty_rate ?? 2); setFaf({ enabled: !!data.faf_enabled, rate: Number(data.faf_rate || 0) }); }).catch(() => {});
    api.get("/coupons").then(({ data }) => setCoupons(data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePure = async () => {
    setPureBusy(true);
    try {
      const { data } = await api.patch("/service/pure-water", { enabled: !pureEnabled });
      setPureEnabled(data.pure_water_enabled);
      toast.success(data.pure_water_enabled ? "Pure Water is now available to order" : "Pure Water turned OFF — customers can't order it");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPureBusy(false); }
  };

  const saveLoyalty = async () => {
    setLoyaltyBusy(true);
    try {
      const { data } = await api.patch("/service/loyalty", { rate: Number(loyaltyRate) });
      setLoyaltyRate(data.loyalty_rate);
      toast.success(`Loyalty credit set to ${data.loyalty_rate}%`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoyaltyBusy(false); }
  };

  const saveFaf = async (next) => {
    setFafBusy(true);
    try {
      const { data } = await api.patch("/service/faf", { enabled: next.enabled, rate: Number(next.rate) });
      setFaf({ enabled: data.faf_enabled, rate: data.faf_rate });
      toast.success(data.faf_enabled ? `Fuel adjustment ON — +${data.faf_rate}% on orders` : "Fuel adjustment turned OFF");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFafBusy(false); }
  };

  const createCoupon = async () => {
    if (!form.code.trim() || !form.value) { toast.error("Enter a code and value"); return; }
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(), type: form.type, value: Number(form.value),
        active: form.active,
        min_amount: form.min_amount ? Number(form.min_amount) : null,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at || null,
      };
      const { data } = await api.post("/coupons", payload);
      setCoupons((c) => [...c, data].sort((a, b) => a.code.localeCompare(b.code)));
      setForm({ code: "", type: "percent", value: "", min_amount: "", max_redemptions: "", expires_at: "", active: true });
      toast.success("Coupon created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const toggleActive = async (c) => {
    try {
      const { data } = await api.patch(`/coupons/${c.id}`, { active: !c.active });
      setCoupons((list) => list.map((x) => (x.id === c.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/coupons/${c.id}`); setCoupons((list) => list.filter((x) => x.id !== c.id)); toast.success("Coupon deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      {/* PURE WATER STOCK */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="pure-stock-panel">
        <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><Droplets className="w-5 h-5 text-cyan-600" /> Pure Water Availability</h3>
        <p className="text-xs text-slate-500 mb-4">Turn Pure Water off when you're out of stock — customers won't be able to order it (Town Water stays available).</p>
        <div className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3">
          <div>
            <div className="font-semibold text-slate-800 text-sm">Pure Water is {pureEnabled ? "available" : "out of stock"}</div>
            <div className="text-xs text-slate-500">{pureEnabled ? "Customers can order Pure Water" : "Hidden from the booking page"}</div>
          </div>
          <button onClick={togglePure} disabled={pureBusy} data-testid="pure-toggle-btn"
            className={`relative w-14 h-8 rounded-full transition-colors ${pureEnabled ? "bg-green-500" : "bg-slate-300"} disabled:opacity-60`}>
            <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-all ${pureEnabled ? "left-7" : "left-1"}`} />
          </button>
        </div>
      </div>

      {/* LOYALTY */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="loyalty-panel">
        <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><Gift className="w-5 h-5 text-purple-600" /> Loyalty Program</h3>
        <p className="text-xs text-slate-500 mb-4">Customers earn this % of every order as store credit to spend later. Set to 0 to switch it off.</p>
        <div className="flex items-end gap-2">
          <div className="w-36">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Credit earned (%)</label>
            <input type="number" min="0" max="100" step="0.5" value={loyaltyRate} onChange={(e) => setLoyaltyRate(e.target.value)} data-testid="loyalty-rate-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
          </div>
          <button onClick={saveLoyalty} disabled={loyaltyBusy} data-testid="loyalty-save-btn"
            className="brand-gradient text-white font-semibold px-5 py-2 rounded-lg text-sm disabled:opacity-60">{loyaltyBusy ? "…" : "Save"}</button>
        </div>
      </div>

      {/* FUEL ADJUSTMENT FACTOR */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="faf-panel">
        <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><Fuel className="w-5 h-5 text-orange-600" /> Fuel Adjustment Factor (FAF)</h3>
        <p className="text-xs text-slate-500 mb-4">Add a temporary fuel surcharge as a % of the water cost. Turn it on when fuel prices spike; turn it off to remove it from all new orders.</p>
        <div className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 mb-3">
          <div>
            <div className="font-semibold text-slate-800 text-sm">Fuel adjustment is {faf.enabled ? "ON" : "OFF"}</div>
            <div className="text-xs text-slate-500">{faf.enabled ? `+${faf.rate}% added to every new order` : "No surcharge applied"}</div>
          </div>
          <button onClick={() => saveFaf({ enabled: !faf.enabled, rate: faf.rate })} disabled={fafBusy} data-testid="faf-toggle-btn"
            className={`relative w-14 h-8 rounded-full transition-colors ${faf.enabled ? "bg-green-500" : "bg-slate-300"} disabled:opacity-60`}>
            <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-all ${faf.enabled ? "left-7" : "left-1"}`} />
          </button>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-36">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Surcharge (%)</label>
            <input type="number" min="0" max="100" step="0.5" value={faf.rate} onChange={(e) => setFaf({ ...faf, rate: e.target.value })} data-testid="faf-rate-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500" />
          </div>
          <button onClick={() => saveFaf({ enabled: faf.enabled, rate: faf.rate })} disabled={fafBusy} data-testid="faf-save-btn"
            className="brand-gradient text-white font-semibold px-5 py-2 rounded-lg text-sm disabled:opacity-60">{fafBusy ? "…" : "Save"}</button>
        </div>
      </div>

      {/* COUPONS */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="coupons-panel">
        <h3 className="font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><Ticket className="w-5 h-5 text-blue-600" /> Coupon Codes</h3>
        <p className="text-xs text-slate-500 mb-4">Create discount codes customers can apply at checkout.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          <Input label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} testid="coupon-code-input" placeholder="SAVE10" />
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="coupon-type-select"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="percent">% off</option>
              <option value="fixed">$ off</option>
            </select>
          </div>
          <Input label={form.type === "percent" ? "Percent (%)" : "Amount ($)"} value={form.value} onChange={(v) => setForm({ ...form, value: v })} testid="coupon-value-input" type="number" placeholder={form.type === "percent" ? "10" : "20"} />
          <Input label="Min spend ($, optional)" value={form.min_amount} onChange={(v) => setForm({ ...form, min_amount: v })} testid="coupon-min-input" type="number" placeholder="0" />
          <Input label="Usage limit (optional)" value={form.max_redemptions} onChange={(v) => setForm({ ...form, max_redemptions: v })} testid="coupon-max-input" type="number" placeholder="∞" />
          <Input label="Expires (optional)" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} testid="coupon-expires-input" type="date" />
        </div>
        <button onClick={createCoupon} disabled={creating} className="brand-gradient text-white font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60 mb-5" data-testid="coupon-create-btn">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Create coupon</>}
        </button>

        <div className="space-y-2">
          {coupons.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No coupons yet.</p>}
          {coupons.map((c) => (
            <div key={c.id} className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3" data-testid={`coupon-${c.code}`}>
              <div className="min-w-0">
                <div className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2">
                  {c.code}
                  <span className="text-xs font-semibold text-blue-600">{c.type === "percent" ? `${c.value}% off` : `$${c.value} off`}</span>
                  {!c.active && <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">INACTIVE</span>}
                </div>
                <div className="text-xs text-slate-500">
                  {c.min_amount ? `Min $${c.min_amount} · ` : ""}{c.max_redemptions ? `${c.times_redeemed}/${c.max_redemptions} used · ` : `${c.times_redeemed} used · `}{c.expires_at ? `expires ${c.expires_at}` : "no expiry"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => toggleActive(c)} title={c.active ? "Deactivate" : "Activate"} data-testid={`coupon-toggle-${c.code}`}
                  className={`p-2 rounded-lg ${c.active ? "text-green-600 hover:bg-green-50" : "text-slate-400 hover:bg-slate-100"}`}><Power className="w-4 h-4" /></button>
                <button onClick={() => remove(c)} className="p-2 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50" data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Input = ({ label, value, onChange, testid, type = "text", placeholder }) => (
  <div>
    <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white" />
  </div>
);
